import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { X, Eye, CreditCard, Activity, DollarSign, TrendingUp, Clock } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";

interface CostAnalysis {
    tasksCompleted: number;
    totalRevenue: number;
    totalCreditsReceived: number;
    currentCredits: number;
    creditsUsed: number;
}

interface UserViewData {
    profile: any;
    credits: any;
    tasks: any[];
    tasksCount: number;
    purchases: any[];
    transactions: any[];
    costAnalysis: CostAnalysis;
}

interface UserViewModeProps {
    data: UserViewData;
    onClose: () => void;
}

export function UserViewMode({ data, onClose }: UserViewModeProps) {
    const [activeTab, setActiveTab] = useState<"tasks" | "purchases" | "transactions">("tasks");

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString("pt-BR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat("pt-BR", {
            style: "currency",
            currency: "BRL",
        }).format(amount);
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case "completed":
                return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
            case "error":
                return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
            case "processing":
            case "generating_image":
            case "generating_video":
                return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
            case "pending":
                return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
            default:
                return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm">
            {/* Admin Mode Banner */}
            <div className="bg-amber-500 text-black py-2 px-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Eye className="w-4 h-4" />
                    <span className="font-medium">
                        Modo Admin - Visualizando como: {data.profile?.full_name || "Usuário"}
                    </span>
                    <Badge variant="secondary" className="text-xs">
                        Somente Leitura
                    </Badge>
                </div>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={onClose}
                    className="text-black hover:bg-amber-600"
                >
                    <X className="w-4 h-4 mr-1" />
                    Sair
                </Button>
            </div>

            <ScrollArea className="h-[calc(100vh-48px)]">
                <div className="container max-w-7xl mx-auto p-6 space-y-6">
                    {/* User Info Header */}
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-2xl font-bold">{data.profile?.full_name || "Usuário"}</h1>
                            <p className="text-muted-foreground">
                                Membro desde {formatDate(data.profile?.created_at || new Date().toISOString())}
                            </p>
                        </div>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid gap-4 md:grid-cols-4">
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-medium flex items-center gap-2">
                                    <CreditCard className="w-4 h-4" />
                                    Créditos Atuais
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-3xl font-bold">{data.credits?.available_credits || 0}</p>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-medium flex items-center gap-2">
                                    <Activity className="w-4 h-4" />
                                    Tasks Criadas
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-3xl font-bold">{data.tasksCount || 0}</p>
                                <p className="text-xs text-muted-foreground">
                                    {data.costAnalysis.tasksCompleted} completadas
                                </p>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-medium flex items-center gap-2">
                                    <DollarSign className="w-4 h-4" />
                                    Total Pago
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-3xl font-bold">{formatCurrency(data.costAnalysis.totalRevenue)}</p>
                                <p className="text-xs text-muted-foreground">
                                    {data.costAnalysis.totalCreditsReceived} créditos recebidos
                                </p>
                            </CardContent>
                        </Card>

                        <Card className="border-amber-200 bg-amber-50 dark:bg-amber-950/20">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-medium flex items-center gap-2">
                                    <TrendingUp className="w-4 h-4" />
                                    Análise de Custo
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-lg font-bold">
                                    {data.costAnalysis.creditsUsed} / {data.costAnalysis.totalCreditsReceived}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                    Créditos usados / recebidos
                                </p>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Tabs */}
                    <div className="flex gap-2 border-b">
                        <Button
                            variant={activeTab === "tasks" ? "default" : "ghost"}
                            onClick={() => setActiveTab("tasks")}
                            size="sm"
                        >
                            Tasks ({data.tasks?.length || 0})
                        </Button>
                        <Button
                            variant={activeTab === "purchases" ? "default" : "ghost"}
                            onClick={() => setActiveTab("purchases")}
                            size="sm"
                        >
                            Compras ({data.purchases?.length || 0})
                        </Button>
                        <Button
                            variant={activeTab === "transactions" ? "default" : "ghost"}
                            onClick={() => setActiveTab("transactions")}
                            size="sm"
                        >
                            Transações ({data.transactions?.length || 0})
                        </Button>
                    </div>

                    {/* Tab Content */}
                    {activeTab === "tasks" && (
                        <div className="space-y-3">
                            {data.tasks?.length === 0 ? (
                                <Card>
                                    <CardContent className="py-8 text-center text-muted-foreground">
                                        Nenhuma task encontrada
                                    </CardContent>
                                </Card>
                            ) : (
                                data.tasks?.map((task: any) => (
                                    <Card key={task.id}>
                                        <CardContent className="py-4">
                                            <div className="flex items-start justify-between">
                                                <div className="space-y-1">
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-medium">{task.product_name}</span>
                                                        <Badge className={getStatusColor(task.status)}>
                                                            {task.status}
                                                        </Badge>
                                                    </div>
                                                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                                                        <Clock className="w-3 h-3" />
                                                        {formatDate(task.created_at)}
                                                    </p>
                                                    {task.error_message && (
                                                        <p className="text-xs text-red-600 dark:text-red-400 mt-2">
                                                            Erro: {task.error_message}
                                                        </p>
                                                    )}
                                                </div>
                                                <span className="text-xs text-muted-foreground">
                                                    {task.current_step || "—"}
                                                </span>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))
                            )}
                        </div>
                    )}

                    {activeTab === "purchases" && (
                        <div className="space-y-3">
                            {data.purchases?.length === 0 ? (
                                <Card>
                                    <CardContent className="py-8 text-center text-muted-foreground">
                                        Nenhuma compra encontrada
                                    </CardContent>
                                </Card>
                            ) : (
                                data.purchases?.map((purchase: any) => (
                                    <Card key={purchase.id}>
                                        <CardContent className="py-4">
                                            <div className="flex items-center justify-between">
                                                <div className="space-y-1">
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-medium">+{purchase.credits_added} créditos</span>
                                                        <Badge
                                                            className={
                                                                purchase.payment_status === "completed"
                                                                    ? "bg-green-100 text-green-800"
                                                                    : "bg-yellow-100 text-yellow-800"
                                                            }
                                                        >
                                                            {purchase.payment_status}
                                                        </Badge>
                                                        {purchase.refunded_at && (
                                                            <Badge variant="destructive">Reembolsado</Badge>
                                                        )}
                                                    </div>
                                                    <p className="text-xs text-muted-foreground">
                                                        {formatDate(purchase.created_at)}
                                                    </p>
                                                </div>
                                                <span className="font-bold">
                                                    {formatCurrency(purchase.amount_paid || 0)}
                                                </span>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))
                            )}
                        </div>
                    )}

                    {activeTab === "transactions" && (
                        <div className="space-y-3">
                            {data.transactions?.length === 0 ? (
                                <Card>
                                    <CardContent className="py-8 text-center text-muted-foreground">
                                        Nenhuma transação encontrada
                                    </CardContent>
                                </Card>
                            ) : (
                                data.transactions?.map((tx: any) => (
                                    <Card key={tx.id}>
                                        <CardContent className="py-4">
                                            <div className="flex items-center justify-between">
                                                <div className="space-y-1">
                                                    <span className="font-medium">{tx.description || tx.type}</span>
                                                    <p className="text-xs text-muted-foreground">
                                                        {formatDate(tx.created_at)}
                                                    </p>
                                                </div>
                                                <span
                                                    className={`font-bold ${tx.amount > 0 ? "text-green-600" : "text-red-600"
                                                        }`}
                                                >
                                                    {tx.amount > 0 ? "+" : ""}
                                                    {tx.amount}
                                                </span>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))
                            )}
                        </div>
                    )}
                </div>
            </ScrollArea>
        </div>
    );
}
